import type { ComponentType, CSSProperties } from "react";
import { ChartArea, ChartNetwork, CircleHelp, SquareChartGantt } from "lucide-react";
import { DASHBOARD_ROUTE_CONFIG } from "./DashboardRoutes";

type IconComponent = ComponentType<{ className?: string; style?: CSSProperties }>;

export interface BreadcrumbIcon {
  src?: string;
  Icon?: IconComponent;
  alt?: string;
  className?: string;
  style?: CSSProperties;
}

interface BreadcrumbText {
  textKey: string;
  before: string;
  after: string;
}

export interface BreadcrumbEntry {
  paths?: readonly string[];
  matches?: (_pathname: string) => boolean;
  lead: BreadcrumbIcon;
  leadLabelKey?: string;
  prefix?: BreadcrumbText & { tag: "p" | "div" };
  trail?: BreadcrumbIcon;
  trailGap?: boolean;
  title?: BreadcrumbText & { className: string };
}

const NBSP = " ";
const ACCENT_STYLE: CSSProperties = { height: "20px", color: "#2C68AD", stroke: "#2C68AD" };
const SMALL_ICON_STYLE: CSSProperties = { height: "20px" };
const INLINE_END_STYLE: CSSProperties = { marginInlineEnd: "7px" };

const SALES_ROUTE = DASHBOARD_ROUTE_CONFIG[280].route;
const WEB_ROUTE = DASHBOARD_ROUTE_CONFIG[293].route;
const ORGANIC_ROUTE = DASHBOARD_ROUTE_CONFIG[301].route;
const CLTV_ROUTE = DASHBOARD_ROUTE_CONFIG[296].route;
const PAID_MEDIA_ROUTE = DASHBOARD_ROUTE_CONFIG[302].route;
const ATTRIBUTION_ROUTE = DASHBOARD_ROUTE_CONFIG[303].route;
const SALES_PRODUCT_ROUTE = DASHBOARD_ROUTE_CONFIG[304].route;
const OMNICHANNEL_ROUTE = DASHBOARD_ROUTE_CONFIG[3].route;

const dashboardLead: BreadcrumbIcon = {
  src: "/Icons/dashboard.svg",
  alt: "dashboard",
  className: "dashboard-image",
};

const chartIcon = (src: string, alt = "dashboard"): BreadcrumbIcon => ({
  src,
  alt,
  className: "chart-image",
  style: SMALL_ICON_STYLE,
});

const dashboardsPrefix = (after: string, tag: "p" | "div" = "div"): BreadcrumbEntry["prefix"] => ({
  tag,
  textKey: "sidebar.sections.dashboards",
  before: "",
  after,
});

const DASHBOARDS_PREFIX = dashboardsPrefix(` ${NBSP}${NBSP} / `);

const dashboardTitle = (textKey: string): BreadcrumbEntry["title"] => ({
  textKey,
  className: "dashboards-heading",
  before: " ",
  after: "",
});

const dashboardEntry = (
  paths: readonly string[],
  trail: BreadcrumbIcon,
  titleKey: string
): BreadcrumbEntry => ({
  paths,
  lead: dashboardLead,
  prefix: DASHBOARDS_PREFIX,
  trail,
  title: dashboardTitle(`homepageHeader.dashboardTitles.${titleKey}`),
});

const simpleEntry = (path: string, lead: BreadcrumbIcon, leadLabelKey: string): BreadcrumbEntry => ({
  paths: [path],
  lead,
  leadLabelKey,
});

const sectionPrefix = (textKey: string, after: string): BreadcrumbEntry["prefix"] => ({
  tag: "p",
  textKey,
  before: " ",
  after,
});

const ENGAGEMENT_PREFIX = sectionPrefix("sidebar.sections.customerMarketEngagements", `${NBSP}${NBSP} /`);
const ACCOUNT_PREFIX = sectionPrefix("sidebar.sections.accountSettings", `${NBSP}${NBSP} /`);
const SOCIAL_PREFIX = sectionPrefix("sidebar.sections.socialListeningAgent", `${NBSP}${NBSP}/`);

const engagementLead = (style?: CSSProperties): BreadcrumbIcon => ({
  src: "/Icons/engagement.svg",
  alt: "user",
  className: "dashboard-image",
  style,
});

const engagementEntry = (
  paths: readonly string[],
  lead: BreadcrumbIcon,
  trailSrc: string,
  titleKey: string
): BreadcrumbEntry => ({
  paths,
  lead,
  prefix: ENGAGEMENT_PREFIX,
  trail: chartIcon(trailSrc),
  trailGap: true,
  title: { textKey: titleKey, className: "dashboards-heading", before: " ", after: " " },
});

const accountLead: BreadcrumbIcon = {
  src: "/Icons/account.svg",
  alt: "dashboard",
  className: "dashboard-image",
};

const socialLead: BreadcrumbIcon = { src: "/Icons/add-user.svg", alt: "user", className: "social-icon" };

const socialEntry = (
  matcher: Pick<BreadcrumbEntry, "paths" | "matches">,
  trail: BreadcrumbIcon,
  titleKey: string
): BreadcrumbEntry => ({
  ...matcher,
  lead: socialLead,
  prefix: SOCIAL_PREFIX,
  trail,
  title: { textKey: titleKey, className: "dashboards-heading", before: " ", after: " " },
});

const clipboardTrail = (alt: string): BreadcrumbIcon => chartIcon("/Icons/Clipboard.svg", alt);

export const BREADCRUMB_ENTRIES: readonly BreadcrumbEntry[] = [
  {
    paths: [SALES_ROUTE],
    lead: dashboardLead,
    prefix: dashboardsPrefix(" /", "p"),
    trail: chartIcon("/Icons/sales.svg"),
    title: dashboardTitle("homepageHeader.dashboardTitles.salesAnalytics"),
  },
  dashboardEntry(
    [`${PAID_MEDIA_ROUTE}/meta-ads-report`],
    chartIcon("/Icons/meta.svg"),
    "metaAdsOverview"
  ),
  dashboardEntry([WEB_ROUTE], { Icon: ChartNetwork, className: "chart-image", style: ACCENT_STYLE }, "webAnalytics"),
  dashboardEntry([PAID_MEDIA_ROUTE], chartIcon("/Icons/coins.svg"), "paidMedia"),
  dashboardEntry([ATTRIBUTION_ROUTE], { Icon: ChartArea, className: "chart-image" }, "customerAttribution"),
  dashboardEntry(
    [SALES_PRODUCT_ROUTE],
    { Icon: SquareChartGantt, className: "chart-image", style: ACCENT_STYLE },
    "salesProductAnalytics"
  ),
  dashboardEntry(
    [OMNICHANNEL_ROUTE],
    { Icon: SquareChartGantt, className: "chart-image", style: ACCENT_STYLE },
    "omnichannelAnalytics"
  ),
  dashboardEntry([ORGANIC_ROUTE], { src: "/Icons/organicmaps.svg", className: "chart-image" }, "organicMedia"),
  {
    ...dashboardEntry([CLTV_ROUTE], chartIcon("/Icons/chart-histogram.svg"), "cltvAnalysis"),
    prefix: dashboardsPrefix(` ${NBSP}${NBSP} /`),
  },
  simpleEntry(
    "/account/user-management",
    { src: "/Icons/user.svg", alt: "user", className: "dashboard-image" },
    "sidebar.sections.userManagement"
  ),
  simpleEntry(
    "/account/help-and-support",
    {
      Icon: CircleHelp,
      className: "dashboard-image",
      style: { height: "20px", width: "20px", color: "#2C68AD", stroke: "#2C68AD" },
    },
    "sidebar.sections.helpAndSupport"
  ),
  simpleEntry("/account/social-listing-agent", socialLead, "sidebar.sections.socialListeningAgent"),
  simpleEntry(
    "/account/ai-chat",
    { src: "/Icons/chatbot-logo.png", alt: "user", className: "dashboard-image-chatbot" },
    "sidebar.sections.askDataTram"
  ),
  simpleEntry(
    "/account/connect-sources",
    { src: "/Icons/dynamic-source.svg", alt: "user", className: "dashboard-image" },
    "sidebar.sections.connectedDataSources"
  ),
  engagementEntry(
    ["/account/engagement/email-analytics-overview"],
    engagementLead(INLINE_END_STYLE),
    "/Icons/users.svg",
    "sidebar.items.overview"
  ),
  engagementEntry(
    [
      "/account/engagement/create-campaign",
      "/account/engagement/campaign-analytics",
      "/account/engagement/campaign-performance",
      "/account/engagement/ai-email-editor",
      "/account/engagement/email-template-editor",
      "/account/engagement/whatsapp-campaign-editor",
      "/account/review-and-send",
      "/account/ai-review-and-send",
      "/account/engagement/compare-campaigns",
    ],
    engagementLead(),
    "/Icons/campaign.svg",
    "sidebar.items.campaigns"
  ),
  engagementEntry(
    [
      "/account/engagement/audience",
      "/account/engagement/select-list-or-segment",
      "/account/engagement/create-segment",
      "/account/engagement/create-list",
      "/account/engagement/modify-file",
    ],
    engagementLead(INLINE_END_STYLE),
    "/Icons/audience-list.svg",
    "sidebar.items.audience"
  ),
  {
    paths: ["/account/my-account/account-settings"],
    lead: { ...accountLead, style: INLINE_END_STYLE },
    prefix: ACCOUNT_PREFIX,
    trail: chartIcon("/Icons/account-settings.svg"),
    trailGap: true,
    title: { textKey: "sidebar.items.myAccount", className: "dashboards-headings", before: " ", after: "" },
  },
  {
    paths: ["/account/my-account/notification-preferences"],
    lead: accountLead,
    prefix: ACCOUNT_PREFIX,
    trail: { src: "/Icons/options.svg", alt: "dashboard", className: "top-sub-icons chart-image" },
    title: {
      textKey: "sidebar.items.notificationPreferences",
      className: "dashboards-headings",
      before: "",
      after: "",
    },
  },
  {
    paths: ["/account/my-account/all-notifications"],
    lead: accountLead,
    prefix: ACCOUNT_PREFIX,
    trail: {
      src: "/Icons/bell.svg",
      alt: "dashboard",
      className: "top-sub-icons chart-image ",
      style: INLINE_END_STYLE,
    },
    title: {
      textKey: "sidebar.items.allNotifications",
      className: "dashboards-headings",
      before: "",
      after: "",
    },
  },
  socialEntry(
    { paths: ["/account/social-listing-agent/brand-analysis"] },
    { src: "/Icons/graph.svg", alt: "analysis", className: "social-icon" },
    "socialListingAgent.historyPage.analysisTypeBrand"
  ),
  socialEntry(
    { paths: ["/account/social-listing-agent/competitive-analysis"] },
    chartIcon("/Icons/Globe-card.svg", "competitive"),
    "socialListingAgent.competitiveAnalysisTitle"
  ),
  socialEntry(
    {
      matches: pathname =>
        pathname.startsWith("/account/social-listing-agent/") &&
        !pathname.includes("brand-analysis") &&
        !pathname.includes("competitive-analysis") &&
        !pathname.includes("history"),
    },
    clipboardTrail("competitive"),
    "socialListingAgent.report"
  ),
  socialEntry(
    { paths: ["/account/social-listing-agent/history"] },
    clipboardTrail("history"),
    "socialListingAgent.historyPage.title"
  ),
];
